// LAN-1321: steps inside the Paddle checkout overlay on web.
//
// `checkout_started` (our click) and `langua_trial_start` (the Paddle webhook)
// say nothing about what happens in between, so an Android user whose card is
// declined looks the same as one who closes the overlay. Paddle.js reports
// those steps through `eventCallback`; this module turns them into one
// `checkout_step` event with a fixed set of properties.
//
// Paddle's payloads carry the customer's email and address and the card's
// last four digits. Nothing is copied from the payload except the fields read
// below, so none of that can reach PostHog. Device, browser and OS come from
// posthog-js itself.
//
// The shared contract lives in docs/analytics/posthog-events.md, "Trial
// decision points".
//
// CommonJS on purpose: the same file is required by the webpack packs and by
// `node --test` in test/javascript/account/paddle_checkout_analytics_test.js.

const EVENT_NAME = 'checkout_step';
const SURFACE_WEB = 'web';

// Paddle event name -> our `step` value. Customer, item and discount events
// are not steps and are dropped. So is `checkout.warning`: it carries no code
// we can read, and in the sandbox it fired on every US checkout.
//
// `completed` means Paddle's overlay showed success, not that a subscription
// exists: a card that failed the bank check still sends it. Count signups from
// `langua_trial_start`.
const STEPS = {
  'checkout.loaded': 'loaded',
  'checkout.payment.selected': 'payment_selected',
  'checkout.payment.initiated': 'payment_initiated',
  'checkout.payment.failed': 'payment_failed',
  'checkout.payment.error': 'payment_error',
  'checkout.error': 'error',
  'checkout.completed': 'completed',
  'checkout.closed': 'closed'
};

// Paddle codes are short snake_case identifiers. Anything else is not copied.
const CODE_PATTERN = /^[a-z0-9_-]{1,64}$/;

function code(value) {
  return typeof value === 'string' && CODE_PATTERN.test(value) ? value : null;
}

// Returns the PostHog properties for a Paddle.js event, or null when the event
// is not a checkout step.
function propertiesFor(event) {
  if (!event || typeof event !== 'object') return null;

  // Own keys only, so a name like `constructor` is not read off the prototype.
  if (!Object.prototype.hasOwnProperty.call(STEPS, event.name)) return null;
  const step = STEPS[event.name];

  const data = event.data && typeof event.data === 'object' ? event.data : null;
  const payment = data && data.payment && typeof data.payment === 'object' ? data.payment : null;
  const methodDetails = payment && payment.method_details && typeof payment.method_details === 'object'
    ? payment.method_details
    : null;

  // Plan, trial and country are already on `checkout_started` and the
  // person, so they are not repeated here.
  return {
    surface: SURFACE_WEB,
    step: step,
    payment_method: methodDetails ? code(methodDetails.type) : null,
    // Only error events carry these, at the top level. `detail` is free text
    // and is never copied.
    error_type: code(event.type),
    error_code: code(event.code)
  };
}

// Paddle.Setup's eventCallback. Looks up posthog when each event arrives, so
// the order in which posthog-js and Paddle.js load does not matter.
//
// Paddle calls this inside its own checkout handling, so the capture is
// deferred off Paddle's call stack: if posthog ever throws, the error is still
// reported as usual but cannot interrupt the checkout. `defer` is injectable
// for tests.
function handlePaddleEvent(event, defer) {
  const properties = propertiesFor(event);
  if (!properties) return;

  (defer || setTimeout)(function () {
    if (typeof window === 'undefined' || !window.posthog || typeof window.posthog.capture !== 'function') return;

    window.posthog.capture(EVENT_NAME, properties);
  }, 0);
}

module.exports = {
  EVENT_NAME: EVENT_NAME,
  propertiesFor: propertiesFor,
  handlePaddleEvent: handlePaddleEvent
};

// Inline view scripts reach the module through this global. See
// app/views/public/langua/subscriptions/free_trial.html.erb and
// app/views/layouts/stream.html.erb (/langua/pro).
if (typeof window !== 'undefined') {
  window.LanguaPaddleCheckout = module.exports;
}
